import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { KpiService } from "../../../services/shipment/kpi.ts";
import {
  KpiShipmentPerformanceFilter,
  KpiShipmentPerformance,
} from "../../../Models/KpiShipmentPerformance.ts";

const kpiService = new KpiService();

interface ShipmentsPerformancesOnTimeProviderProps {
  children: ReactNode;
  filters: KpiShipmentPerformanceFilter;
}

interface ShipmentsPerformancesOnTimeContextValue {
  data: KpiShipmentPerformance[];
  loader: boolean;
  filters: KpiShipmentPerformanceFilter;
}

const ShipmentsPerformancesOnTimeContext = createContext<
  ShipmentsPerformancesOnTimeContextValue | undefined
>(undefined);

const ShipmentsPerformancesOnTimeProvider: React.FC<
  ShipmentsPerformancesOnTimeProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<KpiShipmentPerformance[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentPerformance> | null>(null);

  const prevFilters = useRef<KpiShipmentPerformanceFilter>(filters);

  const getPerformancesOnTime = (): void => {
    setLoader(true);
    kpiService
      .getPerformancesOnTime(filters)
      .then((res) => {
        setLoader(false);
        setData(res?.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getPerformancesOnTime();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getPerformancesOnTime();
  }, []);

  return (
    <ShipmentsPerformancesOnTimeContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsPerformancesOnTimeContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsPerformancesOnTimeContext.
 * Ensures that the context is used only within a ShipmentsPerformancesOnTimeProvider.
 */
const useShipmentsPerformancesOnTimeContext =
  (): ShipmentsPerformancesOnTimeContextValue => {
    const context = useContext(ShipmentsPerformancesOnTimeContext);
    if (!context) {
      throw new Error(
        "useShipmentsPerformancesOnTimeContext must be used within a ShipmentsPerformancesOnTimeProvider"
      );
    }
    return context;
  };

// Export the provider and custom hook for use in other components
export {
  ShipmentsPerformancesOnTimeProvider,
  useShipmentsPerformancesOnTimeContext,
};
